import React from 'react';
import { Field } from '@atlaskit/form';
import Tooltip from '@atlaskit/tooltip';
// @ts-expect-error - TS2614 - Module '"@atlaskit/user-picker"' has no exported member 'LegacyValue'. Did you mean to use 'import LegacyValue from "@atlaskit/user-picker"' instead?
import type { LegacyValue } from '@atlaskit/user-picker';
import { useIntl } from '@atlassian/jira-intl';
import type {
	Group,
	UserPickerOption,
} from '@atlassian/jira-issue-field-group-picker/src/common/types.tsx';
import {
	transformGroupToUserOption,
	transformUserPickerOptionToGroup,
} from '@atlassian/jira-issue-field-group-picker/src/common/utils/index.tsx';
import GroupPickerEdit from '@atlassian/jira-issue-field-group-picker/src/ui/edit/index.tsx';
import type { FieldConfigurationComponentProps } from '../../../../types.tsx';
import type { AssociatedField } from '../../../../types/layout-items.tsx';
import TestIdWrapper from '../../common/test-wrapper/index.tsx';
import { AkFieldWrapper, FieldConfigurationDefaultValue } from '../../styled.tsx';
import messages from './messages.tsx';

export type Props = {
	readonly isMulti?: boolean;
	readonly field: AssociatedField;
	readonly onUpdate: (updatedField: AssociatedField) => void;
};

export const onDefaultValueChange =
	(field: AssociatedField, onUpdate: (updatedField: AssociatedField) => void) =>
	(updatedValue: LegacyValue<UserPickerOption>) => {
		if (Array.isArray(updatedValue)) {
			const groups = updatedValue.map(transformUserPickerOptionToGroup);
			return onUpdate({
				...field,
				defaultValue: groups.length === 0 ? null : groups,
			});
		}
		return onUpdate({
			...field,
			defaultValue: updatedValue ? [transformUserPickerOptionToGroup(updatedValue)] : null,
		});
	};

const toGroup = (defaultValue: unknown): Group | null => {
	if (
		defaultValue === null ||
		typeof defaultValue !== 'object' ||
		!('groupId' in defaultValue) ||
		typeof defaultValue.groupId !== 'string' ||
		!('name' in defaultValue) ||
		typeof defaultValue.name !== 'string'
	) {
		return null;
	}

	return {
		groupId: defaultValue.groupId,
		name: defaultValue.name,
		type: 'group',
	};
};

const refineDefaultValue = (defaultValue: unknown): Array<UserPickerOption> =>
	Array.isArray(defaultValue)
		? defaultValue.map(toGroup).filter(Boolean).map(transformGroupToUserOption)
		: [];

type IsMulti = {
	isMulti: boolean;
};

const GroupFieldConfiguration = ({
	field,
	onUpdate,
	isMulti,
	tooltipDelay,
	testId,
}: FieldConfigurationComponentProps & IsMulti) => {
	const { formatMessage } = useIntl();
	const isModifyDefaultValueDisabled = !field.operations.canModifyDefaultValue;

	const multiGroupDefaultValueLabel = messages.multiGroupDefaultValueLabelNew;
	const groupDefaultValueLabel = messages.groupDefaultValueLabelNew;
	const defaultLabel = isMulti === true ? multiGroupDefaultValueLabel : groupDefaultValueLabel;
	const placeholder = isMulti === true ? messages.multiGroupPlaceholder : messages.groupPlaceholder;
	const disabledTooltipMessage =
		isMulti === true
			? messages.multiGroupDefaultValueDisabledTooltip
			: messages.groupDefaultValueDisabledTooltip;
	const defaultValue = refineDefaultValue(field.defaultValue);

	return (
		<FieldConfigurationDefaultValue>
			<AkFieldWrapper>
				<Field name={`${field.key}-default-value`} label={formatMessage(defaultLabel)}>
					{() => (
						<Tooltip
							content={isModifyDefaultValueDisabled ? formatMessage(disabledTooltipMessage) : null}
							delay={tooltipDelay}
						>
							<TestIdWrapper testId={testId}>
								<GroupPickerEdit
									autoCompleteUrl="/rest/api/3/groups/picker?query="
									// fieldId is optional and used by the underlying UserPicker component in analytics
									// attributes and as a key in SSR. The "field" here means an input element (as in
									// textfield/field in a form). It can be any unique string we want, however we
									// are choosing not to provide it here in case "field" is mistaken as a Jira field
									// at some point in the future (since there may not be a field ID yet for new fields)
									fieldId={null}
									width="100%"
									isMulti={isMulti}
									isDisabled={isModifyDefaultValueDisabled}
									placeholder={formatMessage(placeholder)}
									value={defaultValue}
									onChange={onDefaultValueChange(field, onUpdate)}
								/>
							</TestIdWrapper>
						</Tooltip>
					)}
				</Field>
			</AkFieldWrapper>
		</FieldConfigurationDefaultValue>
	);
};

export const SingleGroupFieldConfiguration = (props: FieldConfigurationComponentProps) => (
	<GroupFieldConfiguration {...props} isMulti={false} />
);

export const MultiGroupFieldConfiguration = (props: FieldConfigurationComponentProps) => (
	<GroupFieldConfiguration {...props} isMulti />
);
